import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { Badge, Card, ExitCandidateSelect, FormSelect } from '@/components/UI';
import type { BaseExitRole } from '@einhaus/project314-types';
import { useAppQuery } from '@/hooks/useAppQuery';
import { haveSameNumberMembers } from '../strategyEditorChanges';
import { buildStrategyCandidateAttachmentUpdate } from './globalExitCandidateDraft';

interface GlobalExitCandidatesCardProps {
    canEdit: boolean;
    strategyId: number;
    /** Reports staged edits so the editor's save bar and section nav can show them. */
    onPendingChangesChange: (changeLabels: string[]) => void;
}

export interface GlobalExitCandidatesCardHandle {
    /** Commits every staged strategy-candidate edit. */
    savePendingChanges: () => Promise<void>;
    /** Drops staged edits and falls back to persisted state. */
    discardPendingChanges: () => void;
}

const GlobalExitCandidatesCard = forwardRef<GlobalExitCandidatesCardHandle, GlobalExitCandidatesCardProps>(
    ({ canEdit, strategyId, onPendingChangesChange }, ref) => {
        const queryClient = useQueryClient();
        const [draftCandidateIds, setDraftCandidateIds] = useState<number[] | null>(null);
        const [draftDefaultCandidateId, setDraftDefaultCandidateId] = useState<number | null | undefined>(undefined);
        const [draftBaseExitRole, setDraftBaseExitRole] = useState<BaseExitRole | undefined>(undefined);

        const resetDrafts = useCallback(() => {
            setDraftCandidateIds(null);
            setDraftDefaultCandidateId(undefined);
            setDraftBaseExitRole(undefined);
        }, []);

        useEffect(() => {
            resetDrafts();
        }, [resetDrafts, strategyId]);

        const strategyAttachmentsQuery = useAppQuery({
            ...trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.queryOptions({
                strategyId
            })
        });

        const replaceStrategyAttachmentsMutation = useMutation({
            ...trpc.tradeLogicExitCandidates.replaceStrategyCandidateAttachments.mutationOptions(),
            onSuccess: async () => {
                setDraftCandidateIds(null);
                setDraftDefaultCandidateId(undefined);
                setDraftBaseExitRole(undefined);
                await Promise.all([
                    queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.pathFilter()),
                    queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getCandidateCoverage.pathFilter()),
                    queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getBestCandidateBacktest.pathFilter()),
                    queryClient.invalidateQueries(trpc.backtest.getStrategyCoverage.pathFilter())
                ]);
            }
        });

        const persistedStrategyCandidates = strategyAttachmentsQuery.data?.candidates ?? [];
        const persistedCandidateIds = persistedStrategyCandidates.map((candidate) => candidate.candidateId);
        const selectedCandidateIds = draftCandidateIds ?? persistedCandidateIds;
        const persistedDefaultCandidateId =
            persistedStrategyCandidates.find((candidate) => candidate.isDefault)?.candidateId ?? selectedCandidateIds[0] ?? null;
        const persistedBaseCandidate = persistedStrategyCandidates.find((candidate) => candidate.isBase) ?? null;
        const persistedBaseCandidateId = persistedBaseCandidate?.candidateId ?? null;
        const persistedBaseExitRole = persistedBaseCandidate?.baseExitRole ?? null;
        const selectedDefaultCandidateId = draftDefaultCandidateId === undefined ? persistedDefaultCandidateId : draftDefaultCandidateId;
        const selectedBaseExitRole = draftBaseExitRole === undefined ? persistedBaseExitRole : draftBaseExitRole;

        const hasCandidateChange = !haveSameNumberMembers(persistedCandidateIds, selectedCandidateIds);
        const hasDefaultChange = selectedDefaultCandidateId !== persistedDefaultCandidateId;
        const hasBaseExitRoleChange = selectedBaseExitRole !== persistedBaseExitRole;

        const changeLabels: string[] = [];

        if (hasCandidateChange) {
            changeLabels.push(`Exit candidates: ${persistedCandidateIds.length} → ${selectedCandidateIds.length} attached`);
        }

        if (hasDefaultChange) {
            changeLabels.push(
                selectedDefaultCandidateId === null ? 'Default candidate cleared' : `Default candidate → #${selectedDefaultCandidateId}`
            );
        }

        if (hasBaseExitRoleChange) {
            changeLabels.push(`Base exit role → ${selectedBaseExitRole?.replace('_', ' ') ?? 'unclassified'}`);
        }

        // Effects must not fire on every render, so the parent is only notified when the labels change.
        const changeLabelsKey = changeLabels.join('|');
        const onPendingChangesChangeRef = useRef(onPendingChangesChange);
        onPendingChangesChangeRef.current = onPendingChangesChange;

        useEffect(() => {
            onPendingChangesChangeRef.current(changeLabelsKey === '' ? [] : changeLabelsKey.split('|'));
        }, [changeLabelsKey]);

        const savePendingChanges = useCallback(async (): Promise<void> => {
            const strategyUpdate = buildStrategyCandidateAttachmentUpdate({
                hasPendingChanges: hasCandidateChange || hasDefaultChange || hasBaseExitRoleChange,
                strategyId,
                selectedCandidateIds,
                selectedDefaultCandidateId,
                persistedBaseCandidateId,
                selectedBaseExitRole
            });

            if (strategyUpdate !== null) {
                await replaceStrategyAttachmentsMutation.mutateAsync(strategyUpdate);
            }
        }, [
            hasCandidateChange,
            hasDefaultChange,
            hasBaseExitRoleChange,
            persistedBaseCandidateId,
            replaceStrategyAttachmentsMutation,
            selectedCandidateIds,
            selectedBaseExitRole,
            selectedDefaultCandidateId,
            strategyId
        ]);

        useImperativeHandle(
            ref,
            () => ({
                discardPendingChanges: resetDrafts,
                savePendingChanges
            }),
            [resetDrafts, savePendingChanges]
        );

        const loadError = strategyAttachmentsQuery.error;
        const isSelectDisabled = !canEdit || strategyAttachmentsQuery.isLoading;

        return (
            <Card
                subtitle="Global candidates are reusable. Attach them to this strategy's catalog, then choose the eligible subset on each portfolio deployment."
                title="Exit candidates"
            >
                <div className="space-y-4">
                    {loadError && (
                        <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100" role="alert">
                            {loadError instanceof Error ? loadError.message : 'The request failed.'}
                        </div>
                    )}
                    {!strategyAttachmentsQuery.isLoading && persistedBaseCandidateId === null && (
                        <div className="rounded-md border border-amber-900/60 bg-amber-950/30 p-3 text-sm text-amber-100" role="alert">
                            This strategy is missing its canonical base exit candidate. Materialize the base candidate before attaching
                            alternatives.
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                        <ExitCandidateSelect
                            fullWidth
                            helpText="Search every active global candidate by PT:650, SL:95, #ID, or AERO variant hash. Attachments are available to standalone research and portfolio deployment selection."
                            isDisabled={isSelectDisabled}
                            label="Strategy candidates"
                            mode="multi"
                            placeholder="Search PT:650, SL:95, #ID, or AERO hash..."
                            value={selectedCandidateIds}
                            onChange={(candidateIds) => {
                                const nextCandidateIds =
                                    persistedBaseCandidateId !== null && !candidateIds.includes(persistedBaseCandidateId)
                                        ? [persistedBaseCandidateId, ...candidateIds]
                                        : candidateIds;
                                setDraftCandidateIds(nextCandidateIds);

                                if (selectedDefaultCandidateId === null || !nextCandidateIds.includes(selectedDefaultCandidateId)) {
                                    setDraftDefaultCandidateId(nextCandidateIds[0] ?? null);
                                }
                            }}
                        />
                        <ExitCandidateSelect
                            fullWidth
                            allowedIds={selectedCandidateIds}
                            helpText="Choose from candidates already attached above. This is the standalone strategy default; portfolio deployments use their own explicit selection."
                            isDisabled={isSelectDisabled || selectedCandidateIds.length === 0}
                            label="Default candidate"
                            mode="single"
                            placeholder="Select a default..."
                            value={selectedDefaultCandidateId}
                            onChange={setDraftDefaultCandidateId}
                        />
                        <FormSelect
                            disabled={isSelectDisabled || persistedBaseCandidateId === null}
                            helpText="Core rules are always combined with AERO candidates. Optional overlays are selected per job. Placeholders remain benchmark-only."
                            label="Base exit role"
                            options={[
                                { value: '', label: 'Classify the base exit', disabled: true },
                                { value: 'strategy_core', label: 'Core strategy exit' },
                                { value: 'optional_overlay', label: 'Optional AERO overlay' },
                                { value: 'placeholder', label: 'Placeholder / benchmark only' }
                            ]}
                            value={selectedBaseExitRole ?? ''}
                            onChange={(event) => {
                                const role = event.target.value;
                                if (role === 'strategy_core' || role === 'optional_overlay' || role === 'placeholder') {
                                    setDraftBaseExitRole(role);
                                }
                            }}
                        />
                    </div>

                    <Badge variant="dark">
                        {selectedCandidateIds.length} attached ·{' '}
                        {selectedDefaultCandidateId === null ? 'no default' : `default #${selectedDefaultCandidateId}`}
                        {persistedBaseCandidateId === null ? '' : ` · current base #${persistedBaseCandidateId}`}
                    </Badge>
                </div>
            </Card>
        );
    }
);

GlobalExitCandidatesCard.displayName = 'GlobalExitCandidatesCard';

export default GlobalExitCandidatesCard;
